import { createAsyncThunk } from "~/app/store/createAsyncThunk";
import { apiGenerateDatasheet, type ProductConfiguratorPDFData } from "../api/apiGenerateDatasheet";
import instance from "~/utils/i18n";
import { CURRENT_LANGUAGE } from "~/utils/constants";
import {
  getConfiguratorFamilyFromProductCode,
  getConfiguratorTypeFromProductCode,
} from "~/domains/configurator/types/ConfiguratorType";
import { XML_TRANSLATION_SHEETS } from "../useXmlConfiguratorTranslations";
import {
  type CarriageDetailsSummaryData,
  isSimpleTable,
  type StepSummaryData,
  type SummaryDataTableV2,
} from "../../Shaft/SummaryDataProvider";
import { getConfiguratorDefinition } from "~/bridges/configurator/configuratorRegistry";
import { removeMultipleTags } from "~/common/utils/domUtils";

export const generateLCDatasheetAction = createAsyncThunk<void, void>(
  "generateLCDatasheet",
  async (_, { getState }) => {
    const state = getState();
    const lcState = state.linearConfigurator;

    const productCode = lcState.productCode;

    const configuratorType = getConfiguratorTypeFromProductCode(productCode);
    const configuratorFamily = getConfiguratorFamilyFromProductCode(productCode);

    const config = XML_TRANSLATION_SHEETS[configuratorType!];
    await instance.loadNamespaces(config.sheetName);
    const rawT = instance.getFixedT(CURRENT_LANGUAGE, config.sheetName);
    const t = (key: string, options?: Record<string, unknown>) =>
      rawT(key, { context: configuratorFamily, ...options }) as string;

    const gaStepData = getConfiguratorDefinition(productCode).stepHardcodedData.map((step) =>
      step.prepareData(state, CURRENT_LANGUAGE),
    );
    const steps = gaStepData.map(transformGaDataToPDFData);

    const data: ProductConfiguratorPDFData = {
      designation: lcState.designation,
      description: removeMultipleTags(t(lcState.description), "span", "p")!,
      imageUrl: t(lcState.productImageUrl),
      brandLogoUrl: "",
      manufacturerLogoUrl: state.linearConfigurator.manufacturerLogo ?? "",
      steps,
    };

    await apiGenerateDatasheet(productCode, data);
  },
);

function transformGaDataToPDFData(stepData: StepSummaryData): ProductConfiguratorPDFData["steps"][number] {
  return {
    title: stepData.name,
    substepData: stepData.tables.flatMap(transformSummaryDataToSubstepData),
  };
}

/**
 * Transforms an array of SummaryData into a SubstepData object.
 * Each SummaryData item becomes a row with two cells: name and value,
 * both sharing the same standard flag.
 */
function transformSummaryDataToSubstepData(data: SummaryDataTableV2 | CarriageDetailsSummaryData): {
  name: string;
  headerRow: string[];
  dataRows: { value: string; standard: boolean }[][];
}[] {
  if ("tables" in data) {
    return data.tables.flatMap((table) => transformSummaryDataToSubstepData({ ...table, name: "" }));
  }
  if (isSimpleTable(data)) {
    return [
      {
        name: data.name,
        headerRow: [],
        dataRows: data.entries.map((item) => [
          { value: item.name, standard: false },
          { value: item.value, standard: item.isStandard ?? false },
        ]),
      },
    ];
  } else {
    const dataRows = [];
    const rowAmount = data.entries[0].values.length;
    for (let rowIndex = 0; rowIndex < rowAmount; rowIndex++) {
      dataRows.push([
        { value: data.entries[0].values[rowIndex].name, standard: false },
        ...data.entries.map((item) => ({
          value: item.values[rowIndex].value,
          standard: item.values[rowIndex].isStandard ?? false,
        })),
      ]);
    }

    return [
      {
        name: data.name,
        headerRow: data.entries.map((item) => item.name),
        dataRows,
      },
    ];
  }
}
